import Text from '@ingka/text';
import {
  useAuthExpiration,
  useLocale,
  useNavigateWithLocale,
} from '@lotta/self-service/lottery-fe/feature-hooks';
import type {
  OccurrenceModule,
  WinnerCommunicationModule,
} from '@lotta/self-service/lottery-fe/feature-lottery-form';
import {
  CreateLotteryForm,
  LotteryPreConfig,
} from '@lotta/self-service/lottery-fe/feature-lottery-form';
import { FeatureFlagContext } from '@lotta/self-service/lottery-fe/util-feature-flags';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { PageIndicator } from '@lotta/self-service/lottery-fe/ui-common';
import styles from './CreateLottery.module.scss';

/* eslint-disable-next-line */
export interface CreateLotteryProps {}

export const CreateLottery: React.FC<CreateLotteryProps> = () => {
  const featureFlags = useContext(FeatureFlagContext);
  const isTokenExpired = useAuthExpiration();
  const { retailUnit } = useLocale();
  const navigate = useNavigateWithLocale();
  const { t } = useTranslation();
  const { templateId } = useParams();
  const [currentPage, setCurrentPage] = useState<'select-occurrence' | 'create-lottery-form'>(
    'select-occurrence'
  );
  const [occurrenceModule, setOccurrenceModule] = useState<OccurrenceModule | undefined>(undefined);
  const [winnerCommunicationModule, setWinnerCommunicationModule] = useState<
    WinnerCommunicationModule | undefined
  >(undefined);

  const handleLotteryCreated = (lotteryId: string) => navigate(`/lottery/${lotteryId}`);

  const onBackClick = () => navigate('/create');

  if (isTokenExpired) {
    return <Text headingSize="m">{t('lottery-form-auth-expired-text')}</Text>;
  }

  if (
    !templateId ||
    (!featureFlags.swipe && templateId === 'swipe') ||
    (!featureFlags.purchase && templateId === 'monthly-purchase') ||
    (!featureFlags.simpleSignup && templateId === 'simple-signup') ||
    (!featureFlags.purchaseAndSimpleSignup && templateId === 'purchase-and-simple-signup')
  ) {
    return null;
  }

  return (
    <div className={styles['container']} data-testid="CREATE_LOTTERY_CONTAINER">
      <h1 className={styles['header']}>{t('general-create-lottery')}</h1>
      <div className={styles['form-container']}>
        {currentPage === 'select-occurrence' && (
          <LotteryPreConfig
            onFormSubmit={(winnerCommunication, occurrence) => {
              setOccurrenceModule(occurrence);
              setWinnerCommunicationModule(winnerCommunication);
              setCurrentPage('create-lottery-form');
            }}
            wrapFormTitle={(title: string) => (
              <PageIndicator pages={8} currentPage={2} text={title} />
            )}
            onSecondaryAction={onBackClick}
            retailUnit={retailUnit}
          />
        )}
        {currentPage === 'create-lottery-form' &&
          occurrenceModule !== undefined &&
          winnerCommunicationModule !== undefined && (
            <CreateLotteryForm
              templateId={templateId}
              occurrenceModule={occurrenceModule}
              winnerCommunicationModule={winnerCommunicationModule}
              retailUnit={retailUnit}
              onLotteryCreated={handleLotteryCreated}
              onCancelled={() => setCurrentPage('select-occurrence')}
            />
          )}
      </div>
    </div>
  );
};
